import React from 'react';
import PropTypes from 'prop-types';
import { Slider, Typography } from '@material-ui/core';
import { createPerimetroAbdominal, getHistoricoPerimetroAbdominal } from '../../../domain/api/MedicionesApi';
import {
	PASO_PERIMETRO_ABDOMINAL,
	RANGO_PERIMETRO_ABDOMINAL,
	textoRangoPermitido,
	validarPerimetroAbdominal,
	valorSliderPerimetro,
} from '../../utils/HelpValidacionMediciones';
import {
	COLUMNAS_HISTORIAL_PERIMETRO,
	RANGOS_SUGERIDOS_PERIMETRO,
	textoClasificacionPerimetro,
} from '../../utils/HelpMediciones';
import usePanelMedicion from './usePanelMedicion';
import useHistorialMedicion from './useHistorialMedicion';
import HistorialMediciones from './HistorialMediciones';
import PanelMedicion from './PanelMedicion';
import CampoMedicion from './CampoMedicion';
import medicionesStyles from './medicionesStyles';

const VALORES_INICIALES = { perimetro: '' };
const AYUDA = textoRangoPermitido(RANGO_PERIMETRO_ABDOMINAL);

/** Panel de registro de perímetro abdominal: campo y slider sincronizados, en cm. */
const PanelPerimetroAbdominal = ({ modulo, numeroDocumento, evbi, onRegistrado }) => {
	const classes = medicionesStyles();
	const historial = useHistorialMedicion({ cargar: getHistoricoPerimetroAbdominal, numeroDocumento });
	const panel = usePanelMedicion({
		valoresIniciales: VALORES_INICIALES,
		validar: validarPerimetroAbdominal,
		enviar: createPerimetroAbdominal,
		numeroDocumento,
		evbi,
		onExito: () => {
			// Solo con codigoRespuesta '0000': usePanelMedicion no llama onExito en error
			historial.recargar();
			if (onRegistrado) {
				onRegistrado(modulo.id);
			}
		},
	});
	const { min, max, unidad } = RANGO_PERIMETRO_ABDOMINAL;

	const clasificacion = panel.resultado && panel.resultado.ok ? (
		<Typography id="perimetro-clasificacion" component="p" className={classes.clasificacionPerimetro}>
			Clasificación: <strong>{textoClasificacionPerimetro(panel.resultado.respuesta)}</strong>
		</Typography>
	) : null;

	return (
		<PanelMedicion
			modulo={modulo}
			guardando={panel.guardando}
			bloqueado={panel.bloqueado}
			resultado={panel.resultado}
			onGuardar={panel.guardar}
			onLimpiar={panel.limpiar}
			extra={clasificacion}
			pie={
				<HistorialMediciones
					id="perimetro"
					columnas={COLUMNAS_HISTORIAL_PERIMETRO}
					historial={historial}
				/>
			}
		>
			{/* Bloque informativo y estático: no lee el estado del panel */}
			<section
				id="perimetro-rangos-sugeridos"
				aria-labelledby="perimetro-rangos-titulo"
				className={classes.rangosPerimetro}
			>
				<Typography id="perimetro-rangos-titulo" variant="subtitle2" component="h4">
					Rangos sugeridos
				</Typography>
				<ul>
					{RANGOS_SUGERIDOS_PERIMETRO.map((r) => (
						<li key={r.categoria}>
							<strong>{r.categoria}</strong>{`: hombres ${r.hombres}, mujeres ${r.mujeres}`}
						</li>
					))}
				</ul>
				<Typography id="perimetro-rangos-nota" variant="body2" component="p">
					Referencia OMS para adultos. La clasificación del registro la asigna el sistema al guardar, según el género del usuario.
				</Typography>
			</section>
			<CampoMedicion
				id="medicion-perimetro"
				label="Perímetro abdominal"
				unidad={unidad}
				value={panel.valores.perimetro}
				onChange={(texto) => panel.setValor('perimetro', texto)}
				error={panel.errores.perimetro}
				ayuda={AYUDA}
			/>
			<div className={classes.sliderPerimetro}>
				<Slider
					value={valorSliderPerimetro(panel.valores.perimetro)}
					min={min}
					max={max}
					step={PASO_PERIMETRO_ABDOMINAL}
					onChange={(e, valor) => panel.setValor('perimetro', String(valor))}
					aria-label="Ajustar perímetro abdominal"
					getAriaValueText={(valor) => `${valor} ${unidad}`}
				/>
			</div>
		</PanelMedicion>
	);
};

PanelPerimetroAbdominal.propTypes = {
	modulo: PropTypes.shape({
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		descripcion: PropTypes.string,
	}).isRequired,
	numeroDocumento: PropTypes.string,
	evbi: PropTypes.number,
	onRegistrado: PropTypes.func,
};

export default PanelPerimetroAbdominal;
